import { useMemo, type CSSProperties } from "react";
import { range, rand, pick } from "./utils/random";
import { DailyImage } from "./DailyImage";
import { Confetti } from "./effects/Confetti";
import { FallingHearts } from "./effects/FallingHearts";
import { FloatingBows } from "./effects/FloatingBows";
import { SparkleStars } from "./effects/SparkleStars";
import { KittyFaces } from "./effects/KittyFaces";
import { TwinklingStars } from "./effects/TwinklingStars";
import { Strawberries } from "./effects/Strawberries";

const MESSAGES = [
  "happy birthday my love",
  "you are my everything",
  "i love you so much",
  "my favorite person",
  "my whole world",
  "you make me so happy",
  "best girl ever",
  "my pretty girl",
  "i'm so lucky",
  "forever yours",
  "always & always",
  "my heart is yours",
  "you light up my life",
  "my sunshine ☀",
  "made for you",
  "you are magic",
  "my one and only",
  "the best day ever",
  "a whole 28th of november for you",
  "i'd marry you tomorrow",
  "every day with you",
  "the loveliest girl",
  "my reason for everything",
  "happy birthday princess",
  "you, you, you ♡",
];

const COLORS = ["#ff5a8a", "#ff3e6c", "#d62a55", "#ff85a1", "#a78bff"];

// Curated subset — heavier effects (GlitterRain, HeartCursor, FloatingBubbles,
// CherryBlossoms, Butterflies, RainbowPulse) are excluded from chaos to keep
// the page smooth when many particle systems run at once. Counts are tuned
// so total particles stay around ~110 instead of the ~500+ we'd get from
// running every effect at full count.
const CHAOS_EFFECTS = [
  { Component: Confetti, count: 24 },
  { Component: FallingHearts, count: 16 },
  { Component: FloatingBows, count: 9 },
  { Component: SparkleStars, count: 22 },
  { Component: KittyFaces, count: 9 },
  { Component: TwinklingStars, count: 14 },
  { Component: Strawberries, count: 10 },
] as const;

export function ChaosMode() {
  const messages = useMemo(
    () =>
      range(18).map(i => ({
        id: i,
        text: pick(MESSAGES),
        left: `${rand(2, 92)}vw`,
        top: `${rand(4, 92)}vh`,
        size: `${rand(1.4, 2.8)}rem`,
        rot: rand(-22, 22),
        color: pick(COLORS),
        duration: `${rand(2.5, 5)}s`,
        delay: `${rand(-4, 0)}s`,
      })),
    [],
  );

  return (
    <>
      {CHAOS_EFFECTS.map(({ Component, count }, i) => (
        <Component key={i} count={count} />
      ))}

      <div className="chaos-overlay">
        <div style={{ textAlign: "center", padding: "0 1rem" }}>
          <h1 className="chaos-title">happy&nbsp;birthday!</h1>
          <p className="chaos-subtitle">my love, my world, my everything ♡</p>
          <div style={{ marginTop: "2rem", display: "inline-block" }}>
            <DailyImage caption="today is your day ♡" />
          </div>
        </div>
      </div>

      <div className="fx-layer" style={{ zIndex: 7 }} aria-hidden>
        {messages.map(m => (
          <span
            key={m.id}
            className="chaos-message"
            style={
              {
                left: m.left,
                top: m.top,
                fontSize: m.size,
                color: m.color,
                "--rot": `${m.rot}deg`,
                transform: `rotate(${m.rot}deg)`,
                animationDuration: m.duration,
                animationDelay: m.delay,
              } as CSSProperties
            }
          >
            {m.text}
          </span>
        ))}
      </div>
    </>
  );
}
